import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Award,
  TrendingUp,
  TrendingDown,
  GitCompare,
  AlertCircle,
  AlertTriangle,
  Lightbulb,
  Target,
  Check,
  ExternalLink,
  ChevronRight,
  Timer,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Separator } from '../ui/separator';
import logger from '../../lib/logger';
import api from '../../lib/api';
import { fmtOrg, tzSuffix } from '../../lib/orgTime';
import { useOrgTz } from '../../hooks/useOrgTz';
import { useHeaderTheme } from '../layout/HeaderThemeContext';

const ICON_MAP = {
  'award': Award,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  'git-compare': GitCompare,
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  'timer': Timer,
  'target': Target,
  'lightbulb': Lightbulb,
  'bell': Bell
};

const COLOR_MAP = {
  'emerald': 'text-emerald-600 bg-emerald-50',
  'blue': 'text-blue-600 bg-blue-50',
  'red': 'text-rose-600 bg-rose-50',
  'purple': 'text-purple-600 bg-purple-50',
  'amber': 'text-amber-600 bg-amber-50',
  'indigo': 'text-indigo-600 bg-indigo-50',
  'slate': 'text-slate-600 bg-slate-50'
};

export default function NotificationDropdown() {
  const navigate = useNavigate();
  const tz = useOrgTz();
  const { onDark } = useHeaderTheme();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications/recent?limit=5');
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch (error) {
      logger.error('Error fetching notifications:', error);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications/unread-count');
      setUnreadCount(data.unread_count);
    } catch (error) {
      // 401 here is benign during auth boot — silently swallow.
      if (error?.response?.status !== 401) {
        logger.error('Error fetching unread count:', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  const markRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read?read=true`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      logger.error('Error marking notification as read:', error);
    }
  };

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markRead(notification.id);
    }
    setOpen(false);
    navigate(notification.source_route);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className={`h-5 w-5 ${onDark ? 'text-slate-300' : 'text-slate-600'}`} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center bg-rose-500 text-white text-xs font-bold rounded-full">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notification-dropdown">
        <div className="p-3 border-b">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Notifications</h3>
            {unreadCount > 0 && (
              <Badge className="bg-indigo-100 text-indigo-700">{unreadCount} new</Badge>
            )}
          </div>
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.map((notification) => {
              const IconComponent = ICON_MAP[notification.icon] || Bell;
              const colorClass = COLOR_MAP[notification.color] || COLOR_MAP.slate;
              
              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0 ${
                    !notification.read ? 'bg-indigo-50/50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-1.5 rounded-lg ${colorClass}`}>
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium truncate ${notification.read ? 'text-slate-600' : 'text-slate-900'}`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <div className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {notification.message}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        <span title={`${fmtOrg(notification.created_at, tz)}${tzSuffix(tz)}`} data-testid="notification-time">{timeAgo(notification.created_at)}</span>
                      </p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-slate-300 flex-shrink-0" />
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-8 text-center">
              <Bell className="h-8 w-8 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No notifications</p>
            </div>
          )}
        </div>
        
        <Separator />
        <div className="p-2">
          <Button
            variant="ghost"
            className="w-full justify-center text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
          >
            View All Notifications
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
